<template>
  <div
    ref="taskRef"
    v-clickOutside="save"
    class="relative flex flex-col justify-between rounded-8 p-14 w-full mb-12 min-h-100 bg-primary"
    :class="isDarkMode ? 'border-2 border-grey-500' : 'shadow-event'"
  >
    <EmojiPicker
      v-if="showEmojiPicker"
      ref="emojiPickerRef"
      v-clickOutside="closeEmojiPicker"
      class="top-80 left-0"
      :class="{ '-top-280': isMobile }"
      @selectEmoji="insertEmoji"
      @click.stop
    />

    <div>
      <input
        id="title"
        ref="titleInputRef"
        v-model="task.title"
        placeholder="What’s the task?"
        class="font-Inter w-full mb-10 text-15 bg-transparent outline-none font-bold pr-5 leading-small block"
        maxlength="40"
        type="text"
        @keypress="onKeyPress"
        @keypress.enter="save"
      >

      <input
        id="subTitle"
        ref="subTitleInputRef"
        v-model="task.subTitle"
        placeholder="Describe it in more detail."
        class="w-full text-14 bg-transparent outline-none -mt-5 block"
        maxlength="100"
        type="text"
        @keypress="onKeyPress"
        @keypress.enter="save"
      >

      <div class="border-t border-grey-500 mt-10" />

      <div class="flex items-center mt-10">
        <ButtonModule
          text="Urgent"
          class="anim-color px-10 py-4 rounded-8 mt-5 mr-10 text-13"
          :class="task.isUrgent ? 'bg-error text-white' : 'bg-grey-500 text-dark-600'"
          @click="toggleUrgent"
        />
        <Mentions v-model="task.project" class="pt-5" />
      </div>
    </div>

    <div class="flex justify-between items-center text-12 mt-5">
      <DueDate :current-date="task.dueDate" @toggleDueDate="toggleDueDate" />
      <TagsModal :selected-tags="task.taskTags" :tags="tags" @toggleTag="toggleTaskTag" @updateTag="updateTag" />
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import TagsModal from '@/components/week-planner/TagsModal'
import Mentions from '@/components/week-planner/Mentions'
import EmojiPicker from '@/components/shared/EmojiPicker'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import DueDate from '@/components/task-board/DueDate'

export default defineComponent({
  components: { TagsModal, Mentions, EmojiPicker, ButtonModule, DueDate },

  props: {
    taskRowId: Number,
    bookIds: Array,
    order: { type: Number, default: 9999 }
  },

  emits: ['hideNewTask', 'getTasks'],

  setup (props, { emit }) {
    const { state, commit, getters, dispatch } = useStore()
    const route = useRoute()

    const taskRef = ref(null)
    const titleInputRef = ref(null)
    const subTitleInputRef = ref(null)

    const showEmojiPicker = ref(false)

    onMounted(() => {
      titleInputRef.value.focus()
    })

    const tags = computed(() => state.task.tags.filter(tag => tag.bookId === selectedBook.value?.id))
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const headquarters = computed(() => state.team.headquarters)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isTwilightTheme = computed(() => getters.isTwilightTheme)

    const allBooks = computed(() => {
      const result = state.book.books.filter(b => !b.isSection)
      if (isTeamAccount.value) {
        result.push(headquarters.value)
      }
      return result
    })

    const selectedBook = ref(allBooks.value.find(b => b.id === parseInt(route.params.id)))

    const time = ref('')

    const task = ref({
      title: '',
      subTitle: '',
      project: null,
      taskTags: [],
      isUrgent: false,
      dueDate: null
    })

    async function save () {
      if (task.value.title.trim() === '') {
        emit('hideNewTask')
        return
      }

      commit('toggleBookLoading', true)

      const { data } = await dispatch('createTask', {
        title: task.value.title,
        subTitle: task.value.subTitle,
        additionalInfo: '',
        isUrgent: task.value.isUrgent,
        taskRowId: props.taskRowId,
        bookId: selectedBook.value.id,
        projectId: task.value.project?.id,
        order: props.order,
        dueDate: task.value.dueDate,
        duplicateToEvent: task.value.duplicateToEvent,
        tagIds: task.value.taskTags.map(item => item.id)
      })

      const tasks = state.task.tasks
      tasks.push(data)
      commit('setTasks', tasks)

      emit('hideNewTask')
      commit('toggleBookLoading', false)
    }

    function toggleDueDate ({ date, duplicateToEvent }) {
      task.value.dueDate = date
      task.value.duplicateToEvent = duplicateToEvent
    }

    function toggleTaskTag ({ tag, isSelected }) {
      if (isSelected) {
        task.value.taskTags = task.value.taskTags.filter(t => t.id !== tag.id)
      } else {
        task.value.taskTags.push(tag)
      }
    }

    function updateTag ({ name, id }) {
      dispatch('updateTaskTag', { name, id, bookId: selectedBook.value.id })
        .then(() => emit('getTasks'))
    }

    let focusedInput = ''

    function closeEmojiPicker () {
      showEmojiPicker.value = false
    }

    function onKeyPress (e) {
      if (e.keyCode === 58) {
        focusedInput = e.target.id
        showEmojiPicker.value = true
      } else {
        focusedInput = ''
        showEmojiPicker.value = false
      }
    }

    function insertEmoji (emoji) {
      if (focusedInput === 'title') {
        titleInputRef.value.focus()
        if (task.value.title.length >= 40) return
      } else {
        subTitleInputRef.value.focus()
        if (task.value.subTitle.length >= 100) return
      }

      if (task.value[focusedInput].slice(-1) === ':') {
        task.value[focusedInput] = task.value[focusedInput].slice(0, -1) + emoji.char
      } else {
        task.value[focusedInput] += emoji.char
      }
    }

    function toggleUrgent () {
      task.value.isUrgent = !task.value.isUrgent
    }

    return {
      // refs
      taskRef,
      titleInputRef,
      subTitleInputRef,
      // data
      time,
      tags,
      task,
      showEmojiPicker,
      // computed
      allBooks,
      isMobile,
      isDarkMode,
      selectedBook,
      isTwilightTheme,
      // function
      save,
      updateTag,
      onKeyPress,
      insertEmoji,
      toggleUrgent,
      toggleTaskTag,
      toggleDueDate,
      closeEmojiPicker
    }
  }
})
</script>
